import React, { useState, useEffect, useMemo, useCallback } from "react";
import _ from "lodash";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { getMarketGroupHistory } from "../../../common/services/markets/marketGroupService";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const MarketGroupHistory = ({ marketGroupId }) => {
  const { t } = useTranslation(["markets"]);
  const [marketGroupHistory, setMarketGroupHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);

  // Map API data to view model with formatted dates
  const mapToViewModel = useCallback((marketGroups) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return marketGroups.map((marketGroup) => ({
      id: marketGroup.id,
      name: marketGroup.name,
      active: marketGroup.active,
      updatedByUserAccountId: marketGroup.createdByUserAccountId,
      updatedBy:
        marketGroup.updatedByUserAccountFirstName +
        " " +
        marketGroup.updatedByUserAccountLastName,
      updatedDate: new Date(marketGroup.updatedDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      writeDate: new Date(marketGroup.writeDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      eventId: marketGroup.eventId,
    }));
  }, []);

  // Fetch market group history on component mount
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const data = await getMarketGroupHistory(marketGroupId);
        const formattedData = mapToViewModel(data);
        setMarketGroupHistory(_.orderBy(formattedData, "id", "desc"));
      } catch (error) {
        console.error("Error fetching market group history:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistory();
  }, [marketGroupId, mapToViewModel]);

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "id",
        headerName: t("RecordID"),
        sortable: true,
        filter: true,
        sort: "desc",
        minWidth: 100,
      },
      {
        field: "name",
        headerName: t("MarketGroupName"),
        sortable: true,
        filter: true,
        flex: 2,
        minWidth: 150,
      },
      {
        field: "active",
        headerName: t("Active"),
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        headerClass: "ag-center-aligned-header",
        filterValueGetter: (params) => params.data.activeString,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      {
        field: "updatedBy",
        headerName: t("UpdatedBy"),
        sortable: true,
        filter: true,
        flex: 1.5,
        minWidth: 150,
      },
      {
        field: "updatedDate",
        headerName: t("UpdateDate"),
        sortable: true,
        filter: true,
        minWidth: 160,
      },
      {
        field: "writeDate",
        headerName: t("WriteDate"),
        sortable: true,
        filter: true,
        minWidth: 160,
      },
      {
        field: "eventId",
        headerName: t("EventID"),
        sortable: true,
        filter: true,
        minWidth: 120,
      },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 100,
      flex: 1,
    }),
    []
  );

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      // Available page size options for user to choose
      pageSizeOptions: [5, 10, 20, 50, 100],
    }),
    []
  );

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
  }, []);

  if (isLoading) return <Loading />;

  return (
    <div className="history-table-container">
      <div className="mb-3">
        {t("MarketGroupID")} {marketGroupId}
      </div>
      <ThemeableAgGrid
        className="table-extra-small"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={marketGroupHistory}
        getRowId={(params) => params.data.id.toString()}
        // Use normal layout instead of autoHeight for pagination
        domLayout="autoHeight"
        animateRows={true}
        // Enable pagination
        pagination={true}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={paginationConfig.pageSizeOptions}
        onPaginationChanged={(event) => {
          const newPageSize = event.api.paginationGetPageSize();
          if (newPageSize !== pageSize) {
            handlePageSizeChange(newPageSize);
          }
        }}
        suppressPaginationPanel={false}
        height="400px"
        // Allow horizontal scrolling if needed
        allowHorizontalScroll={true}
      />
    </div>
  );
};

export default MarketGroupHistory;

